import { Component, ErrorInfo, ReactNode } from 'react';
import { translateCurrentShellMessage } from '@/features/language/useI18n';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class EmbeddingErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error in embedded component:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="embed-error">
          <h3>{translateCurrentShellMessage('Failed to load component')}</h3>
          <p>
            {this.state.error?.message || translateCurrentShellMessage('An unexpected error occurred while loading this component.')}
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="btn ghost sm"
            type="button"
          >
            {translateCurrentShellMessage('Try Again')}
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
